import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
    type ReactNode,
} from 'react'
import { UserManager, User, type UserManagerSettings } from 'oidc-client-ts'

/**
 * Auth context — replaces AngularJS `security`, `token`, and `user` services.
 *
 * Uses oidc-client-ts (TypeScript rewrite of the existing oidc-client library)
 * with the same OIDC configuration to maintain session compatibility.
 */

interface AuthContextType {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    login: (extraQueryParams?: Record<string, string>, stateData?: Record<string, unknown>) => Promise<void>
    logout: () => Promise<void>
    getAccessToken: () => string | null
    handleCallback: () => Promise<Record<string, unknown> | undefined>
    handleLogoutCallback: () => Promise<void>
    handleSilentCallback: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const oidcAuthority = import.meta.env.VITE_OIDC_AUTHORITY || ''

// Debug logging for test environment
const isTestEnv = import.meta.env.MODE === 'test'
if (typeof globalThis.window !== 'undefined') {
    console.log('[AuthContext] VITE_OIDC_AUTHORITY from import.meta.env:', import.meta.env.VITE_OIDC_AUTHORITY)
    console.log('[AuthContext] oidcAuthority resolved to:', oidcAuthority)
    console.log('[AuthContext] import.meta.env.DEV:', import.meta.env.DEV)
    console.log('[AuthContext] import.meta.env.PROD:', import.meta.env.PROD)
    console.log('[AuthContext] import.meta.env.MODE:', import.meta.env.MODE)
    console.log('[AuthContext] isTestEnv:', isTestEnv)
}

const defaultOidcSettings: UserManagerSettings = {
    authority: oidcAuthority,
    client_id: import.meta.env.VITE_OIDC_CLIENT_ID || 'sluclient',
    redirect_uri: `${window.location.origin}/#/loginCallback`,
    post_logout_redirect_uri: `${window.location.origin}/#/logoutCallback`,
    silent_redirect_uri: `${window.location.origin}/#/loginSilentCallback`,
    response_type: 'code',
    response_mode: 'query',
    scope: 'ofacapi openid',
    automaticSilentRenew: false,
    monitorSession: false,
    filterProtocolClaims: true,
    loadUserInfo: false,
    // Provide static metadata to avoid OIDC server discovery in dev/test modes.
    // - authorization_endpoint and end_session_endpoint point to the real identity server
    //   (they're full-page redirects, not AJAX calls, so CORS doesn't apply)
    // - token_endpoint uses a relative path proxied through Vite to the identity server
    // - In production, metadata is fetched from the OIDC discovery endpoint
    ...(import.meta.env.DEV || !import.meta.env.PROD ? {
        metadata: {
            issuer: oidcAuthority,
            authorization_endpoint: `${oidcAuthority}/connect/authorize`,
            token_endpoint: '/connect/token',
            end_session_endpoint: `${oidcAuthority}/connect/endsession`,
            jwks_uri: `${oidcAuthority}/.well-known/openid-configuration/jwks`,
        },
    } : {}),
}

interface AuthProviderProps {
    children: ReactNode
    settings?: Partial<UserManagerSettings>
}

export function AuthProvider({ children, settings }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const userManager = useState(
        () => new UserManager({ ...defaultOidcSettings, ...settings }),
    )[0]

    // Tracks whether a signout redirect is in progress.
    // oidc-client-ts calls removeUser() (triggering userUnloaded) BEFORE navigating
    // to the identity server's end_session endpoint. Without this guard, the
    // userUnloaded handler sets user=null → isAuthenticated=false → RequireAuth
    // redirects to /login → LoginPage auto-calls signinRedirect() → re-authenticates
    // before the signout redirect can happen.
    const isLoggingOut = useRef(false)

    useEffect(() => {
        // Check storage for OIDC user before trying to load
        const localKeys = []
        const sessionKeys = []
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key?.startsWith('oidc')) localKeys.push(key)
        }
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i)
            if (key?.startsWith('oidc')) sessionKeys.push(key)
        }
        console.log('[AuthContext] useEffect - storage state on mount:', {
            localStorageOidcKeys: localKeys,
            sessionStorageOidcKeys: sessionKeys,
            authority: oidcAuthority,
            clientId: import.meta.env.VITE_OIDC_CLIENT_ID || 'sluclient',
        })

        // Load user from session on mount.
        // OIDC callback processing is handled by LoginCallbackPage (via handleCallback),
        // NOT here — doing it in both places causes a race condition where two concurrent
        // signinRedirectCallback calls compete, the loser's error handler redirects to
        // /login, and the auth flow loops.
        userManager
            .getUser()
            .then((loadedUser) => {
                console.log('[AuthContext] UserManager.getUser() returned:', {
                    hasUser: !!loadedUser,
                    userId: loadedUser?.profile?.sub,
                    expired: loadedUser?.expired,
                    accessToken: loadedUser?.access_token?.substring(0, 20) + '...',
                })
                if (loadedUser) {
                    // Angular parity: use the token regardless of client-side expiry.
                    // Reference tokens are validated server-side by Identity Server.
                    // If truly expired, the API returns 401 and apiClient redirects to login.
                    console.log('[AuthContext] User found, setting state (expired=%s)', loadedUser.expired)
                    setUser(loadedUser)
                    sessionStorage.setItem('access_token', loadedUser.access_token)
                } else {
                    console.log('[AuthContext] No user in storage')
                }
            })
            .catch((err) => {
                console.error('[AuthContext] UserManager.getUser() error:', err)
            })
            .finally(() => {
                console.log('[AuthContext] useEffect finally, setting isLoading=false')
                setIsLoading(false)
            })

        // Listen for user loaded/unloaded events
        const onUserLoaded = (loadedUser: User) => {
            setUser(loadedUser)
            sessionStorage.setItem('access_token', loadedUser.access_token)
        }
        const onUserUnloaded = () => {
            // Skip React state update during logout — the page is about to navigate
            // to the identity server. Updating state here would trigger RequireAuth
            // → /login → signinRedirect before signoutRedirect can complete.
            if (isLoggingOut.current) return
            setUser(null)
            sessionStorage.removeItem('access_token')
        }

        userManager.events.addUserLoaded(onUserLoaded)
        userManager.events.addUserUnloaded(onUserUnloaded)

        return () => {
            userManager.events.removeUserLoaded(onUserLoaded)
            userManager.events.removeUserUnloaded(onUserUnloaded)
        }
    }, [userManager])

    const login = useCallback(
        (extraQueryParams?: Record<string, string>, stateData?: Record<string, unknown>) => {
            const args: Record<string, unknown> = {}
            if (extraQueryParams && Object.keys(extraQueryParams).length > 0) {
                args.extraQueryParams = extraQueryParams
            }
            if (stateData) {
                args.state = stateData
            }
            return userManager.signinRedirect(Object.keys(args).length > 0 ? args : undefined)
        },
        [userManager],
    )

    const logout = useCallback(
        async () => {
            isLoggingOut.current = true
            sessionStorage.removeItem('access_token')
            return userManager.signoutRedirect()
        },
        [userManager],
    )

    const getAccessToken = useCallback(
        () => user?.access_token ?? null,
        [user],
    )

    // Matches legacy loginCallbackController.js — processes the OIDC redirect response.
    // Extracts OAuth params from hash fragment (see useEffect comment for explanation).
    // Returns OIDC state data if present (used by LoginCallbackPage to restore
    // the original URL, matching Angular's loginCallbackController.js behavior).
    const handleCallback = useCallback(
        async (): Promise<Record<string, unknown> | undefined> => {
            let callbackUrl: string | undefined
            const hash = window.location.hash
            const hashQueryIndex = hash.indexOf('?')
            if (hashQueryIndex !== -1) {
                const hashParams = new URLSearchParams(hash.substring(hashQueryIndex))
                if (hashParams.has('code') && hashParams.has('state')) {
                    callbackUrl = `${window.location.origin}/?${hashParams.toString()}`
                }
            }
            const signinResponse = await userManager.signinRedirectCallback(callbackUrl)
            if (signinResponse) {
                setUser(signinResponse)
                sessionStorage.setItem('access_token', signinResponse.access_token)
            }
            // Return OIDC state data (matches Angular loginCallbackController.js
            // which reads signinResponse.state.originalState)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const stateData = (signinResponse as any)?.state as Record<string, unknown> | undefined
            return stateData ?? undefined
        },
        [userManager],
    )

    // Matches legacy logoutCallbackController.js / logoutState.complete() —
    // processes the OIDC signout redirect response.
    const handleLogoutCallback = useCallback(
        async () => {
            await userManager.signoutRedirectCallback()
        },
        [userManager],
    )

    // Matches legacy loginSilentCallbackController.js — processes silent token renewal
    const handleSilentCallback = useCallback(
        () => userManager.signinSilentCallback(),
        [userManager],
    )

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                login,
                logout,
                getAccessToken,
                handleCallback,
                handleLogoutCallback,
                handleSilentCallback,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export { AuthContext }
